import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * MobileBottomSheet Component
 * 
 * A bottom sheet component for mobile devices that slides up from the bottom.
 * Used for displaying documents, insights, and other content that would be
 * in a right sidebar on desktop.
 * 
 * Features:
 * - Slide up animation with spring physics
 * - Swipe-to-close gesture
 * - Backdrop blur
 * - Body scroll lock when open
 * - Safe area support
 */
export const MobileBottomSheet = ({ isOpen, onClose, title, children, closeButtonId, closeButtonClass }) => {
  useBodyScrollLock(isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: 0.2,
              ease: 'linear'
            }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-[200]"
            data-framer-motion="true"
            style={{
              willChange: 'opacity',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
              pointerEvents: 'auto',
            }}
          />
          
          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ 
              duration: 0.25,
              ease: 'easeOut'
            }}
            className="fixed bottom-0 left-0 right-0 bg-white/10 backdrop-blur-lg rounded-t-3xl z-[201] max-h-[80vh] overflow-hidden"
            data-framer-motion="true"
            style={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              // Hardware acceleration for smooth animations
              willChange: 'transform',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              isolation: 'isolate',
              contain: 'layout style paint',
              pointerEvents: 'auto',
            }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-white/30 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              <button 
                onClick={onClose}
                id={closeButtonId || 'mobileBottomSheetCloseBtn'}
                className={closeButtonClass ? `${closeButtonClass} p-2 rounded-full hover:bg-white/10 active:scale-95 min-w-[48px] min-h-[48px] flex items-center justify-center touch-manipulation` : 'p-2 rounded-full hover:bg-white/10 active:scale-95 min-w-[48px] min-h-[48px] flex items-center justify-center touch-manipulation'}
                aria-label="Close"
              >
                <X size={20} className="text-white" />
              </button>
            </div>
            
            {/* Content - MOBILE: Allow overscroll, smooth scrolling */}
            <div 
              className="overflow-y-auto max-h-[calc(80vh-80px)] p-4 mobile-bottom-sheet-content"
              style={{
                overscrollBehaviorY: 'contain',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileBottomSheet;

